import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Stack, Typography } from '@mui/material';
import { IconRobot } from '@tabler/icons-react';

import MessageBubble from './MessageBubble';

// ==============================|| MESSAGE LIST ||============================== //

// Space kept above a freshly asked question so the tail of the previous answer
// stays visible (a few lines) instead of scrolling entirely out of view — same
// behaviour as ChatGPT / Claude Desktop, which anchor the new question near the
// top but leave the prior context peeking above it. ~4 lines of body text.
const PEEK_ABOVE_QUESTION = 88;

// Greeting shown before the first message. Sizes to its content so it can be
// centered together with the input on an empty conversation.
export const EmptyState = () => (
  <Stack
    spacing={1.5}
    sx={{
      alignItems: 'center',
      px: 3,
      textAlign: 'center'
    }}
  >
    <IconRobot size={40} stroke={1.2} />
    <Typography variant="subtitle1">How can I help?</Typography>
    <Typography
      variant="caption"
      sx={{
        color: 'text.secondary'
      }}
    >
      Ask about devices or firmware, e.g. &quot;Is SN12345 online?&quot; or &quot;What firmware is available for M60-EU?&quot;
    </Typography>
  </Stack>
);

const MessageList = ({ messages, scrollRef }) => {
  const endRef = useRef(null);
  const lastQuestionRef = useRef(null);
  const contentEndRef = useRef(null);
  // Reserved space below the last message so a freshly asked question can scroll
  // all the way to the top even before its (still short) answer has arrived.
  const [spacer, setSpacer] = useState(0);

  // Track only the most recent user question. Keying the scroll effect on this
  // id (instead of the whole messages array) means we reposition when a new
  // question is asked, not on every streaming token of the answer.
  const lastQuestionId = messages.filter((m) => m.role === 'user').pop()?.id;

  // Keep the spacer sized so that (last question → end of real content) plus the
  // spacer always fills at least one viewport. As the answer streams in and
  // grows, the used height increases and the spacer shrinks to match.
  useLayoutEffect(() => {
    const node = lastQuestionRef.current;
    const container = scrollRef?.current;
    const contentEnd = contentEndRef.current;
    if (!node || !container || !contentEnd) return;

    const used = contentEnd.getBoundingClientRect().top - node.getBoundingClientRect().top;
    // Account for the peek space above the question: the question no longer sits
    // at the very top, so it needs one viewport minus that peek below it.
    const needed = Math.max(0, container.clientHeight - PEEK_ABOVE_QUESTION - used);
    // avoid a render loop from sub-pixel jitter
    setSpacer((prev) => (Math.abs(prev - needed) > 1 ? needed : prev));
  }, [messages, scrollRef]);

  useEffect(() => {
    const node = lastQuestionRef.current;
    // react-perfect-scrollbar sets overflow:hidden on its container and drives
    // scrolling itself, so scrollIntoView / smooth scrollTo don't work here —
    // assigning scrollTop directly is the reliable way to move it.
    const container = scrollRef?.current;
    if (!node || !container) {
      endRef.current?.scrollIntoView();
      return undefined;
    }

    // Run after paint so the spacer (set in useLayoutEffect) is already applied
    // and the container has grown enough for the question to reach the top.
    const raf = requestAnimationFrame(() => {
      // Bring the new question near the top of the scroll area, keeping a few
      // lines of the previous answer visible above it so the user can see the
      // conversation continues rather than assuming it was wiped. Clamp to the
      // max scroll offset as a safety net so we degrade to scrolling as far
      // down as possible.
      const offset = node.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop;
      const maxTop = container.scrollHeight - container.clientHeight;
      container.scrollTop = Math.max(0, Math.min(offset - PEEK_ABOVE_QUESTION, maxTop));
    });
    // Only reposition when a new question is asked — not on every streaming
    // token — so the user can freely scroll while the answer grows.
    return () => cancelAnimationFrame(raf);
  }, [lastQuestionId, scrollRef]);

  if (!messages.length) {
    return (
      <Stack
        sx={{
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%'
        }}
      >
        <EmptyState />
      </Stack>
    );
  }

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      {messages.map((m) => (
        <div key={m.id} ref={m.id === lastQuestionId ? lastQuestionRef : undefined}>
          <MessageBubble message={m} />
        </div>
      ))}
      {/* marks the end of real content so the spacer height can be measured */}
      <div ref={contentEndRef} data-testid="chat-content-end" />
      <div aria-hidden data-testid="chat-spacer" style={{ height: spacer }} />
      <div ref={endRef} />
    </Box>
  );
};

MessageList.propTypes = {
  messages: PropTypes.array,
  // ref to the react-perfect-scrollbar scroll container, provided by the drawer
  scrollRef: PropTypes.shape({ current: PropTypes.any })
};

export default MessageList;
